import ReactMarkdown from 'react-markdown';
import { APP } from '../tokens';

const F = APP.fontFamily;

export interface CoTAction {
  label: string;
  icon?: 'edit' | 'arrow';
  primary?: boolean;
  onClick?: () => void;
}

interface CoTAnswerBlockProps {
  content: string;
  actions?: CoTAction[];
  /** True when the run produced a result but some steps failed */
  partialCompletion?: boolean;
  /** Titles of steps that failed (for partial completion explanation) */
  failedStepTitles?: string[];
  /** True when the answer is still being streamed (not yet finalized). */
  streaming?: boolean;
}

function CornerLogo() {
  return (
    <svg width={18} height={18} viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="corner-cot-grad" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
          <stop stopColor="#D46A3A" />
          <stop offset="1" stopColor="#8B3A10" />
        </linearGradient>
      </defs>
      <path
        d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152"
        stroke="url(#corner-cot-grad)"
        strokeWidth="40"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#FDFCFB" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#FDFCFB" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
      <path d="M5 12h14" />
      <path d="M12 5l7 7-7 7" />
    </svg>
  );
}

export default function CoTAnswerBlock({ content, actions, partialCompletion, failedStepTitles, streaming }: CoTAnswerBlockProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 8, gap: 8 }}>
      {/* Header: Corner logo + label + partial indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <CornerLogo />
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: APP.textPrimary,
            lineHeight: '16px',
            fontFamily: F,
          }}
        >
          Corner
        </span>
        {streaming && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: '#D97645',
              lineHeight: '14px',
              fontFamily: F,
              animation: 'cotSpin 1.2s linear infinite',
            }}
          >
            Generating...
          </span>
        )}
        {partialCompletion && !streaming && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: '#D46A3A',
              lineHeight: '14px',
              fontFamily: F,
              backgroundColor: 'rgba(212, 106, 58, 0.08)',
              padding: '1px 6px',
              borderRadius: 4,
            }}
          >
            Partial result
          </span>
        )}
      </div>

      {/* Partial completion explanation */}
      {partialCompletion && failedStepTitles && failedStepTitles.length > 0 && (
        <div
          style={{
            fontSize: 11,
            color: APP.textCardDetail,
            lineHeight: '16px',
            fontFamily: F,
            padding: '6px 10px',
            backgroundColor: 'rgba(212, 106, 58, 0.04)',
            borderRadius: 6,
            border: '1px solid rgba(212, 106, 58, 0.1)',
          }}
        >
          {failedStepTitles.length === 1
            ? `The "${failedStepTitles[0]}" step couldn't be completed. Here's what we were able to do.`
            : `${failedStepTitles.length} steps couldn't be completed: ${failedStepTitles.join(', ')}. Here's what we were able to do.`}
        </div>
      )}

      {/* Body text — rendered as markdown for structured doc analyses */}
      <div
        className="corner-md"
        style={{
          fontSize: 13,
          fontWeight: 400,
          color: APP.textCardBody,
          lineHeight: '20px',
          fontFamily: F,
        }}
      >
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>

      {/* Action chips */}
      {actions && actions.length > 0 && (
        <div style={{ display: 'flex', paddingTop: 4, gap: 8 }}>
          {actions.map((action) =>
            action.primary ? (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 6,
                  paddingBlock: 5,
                  paddingInline: 12,
                  gap: 5,
                  backgroundColor: APP.textDark,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: F,
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#FDFCFB',
                  lineHeight: '16px',
                }}
              >
                {action.icon === 'edit' && <EditIcon />}
                {action.label}
                {action.icon === 'arrow' && <ArrowIcon />}
              </button>
            ) : (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 6,
                  paddingBlock: 5,
                  paddingInline: 12,
                  backgroundColor: 'transparent',
                  border: `1px solid ${APP.border}`,
                  cursor: 'pointer',
                  fontFamily: F,
                  fontSize: 12,
                  fontWeight: 400,
                  color: APP.textCardBody,
                  lineHeight: '16px',
                }}
              >
                {action.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}
